/**
 * ValutaGenio · /percorso — "Come funziona"
 *
 * Single-purpose narrative page that explains the CAPISCI → VALUTA →
 * DECIDI → AGISCI journey. All backend endpoints, owner journey
 * routes and email templates are untouched. This page only wires
 * navigation to /vg2 and /#inizia (homepage hero anchor).
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Compass,
  Ruler,
  GitCompareArrows,
  ClipboardList,
  HelpCircle,
} from "lucide-react";
import { LandingNavbar } from "@/pages/OwnerLanding";
import Footer from "@/components/landing/Footer";

// ---------- shared building blocks ---------------------------------------

function Eyebrow({ children, testid }) {
  return (
    <div
      data-testid={testid}
      className="uppercase tracking-[0.18em] text-[11px] md:text-xs font-semibold text-[#B40101]"
    >
      {children}
    </div>
  );
}

function SectionTitle({ children, testid, size = "lg" }) {
  const cls =
    size === "xl"
      ? "text-4xl sm:text-5xl md:text-6xl lg:text-[64px] leading-[1.05]"
      : "text-3xl sm:text-4xl md:text-5xl lg:text-[46px] leading-[1.1]";
  return (
    <h2
      data-testid={testid}
      className={`font-display font-extrabold text-[#1F2937] tracking-tight ${cls}`}
    >
      {children}
    </h2>
  );
}

// ---------- 1. APERTURA ---------------------------------------------------

function OpeningSection() {
  return (
    <section
      data-testid="percorso-opening"
      className="bg-white border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-16 md:py-24 lg:py-28">
        <Eyebrow testid="percorso-opening-eyebrow">Come funziona</Eyebrow>
        <h1
          data-testid="percorso-opening-h1"
          className="mt-4 md:mt-6 font-display font-extrabold text-[#1F2937] tracking-tight text-4xl sm:text-5xl md:text-6xl lg:text-[64px] leading-[1.05]"
        >
          Capisci. Valuta. Decidi. Agisci.
        </h1>
        <p
          data-testid="percorso-opening-lede"
          className="mt-6 md:mt-8 text-[#1F2937] text-lg md:text-xl leading-relaxed font-light max-w-2xl"
        >
          Un percorso per scoprire il valore e il potenziale della tua casa,
          confrontare le strade possibili e arrivare a un piano concreto.
        </p>
        <div className="mt-8 md:mt-10 flex items-center gap-4">
          <Link
            to="/vg2"
            data-testid="percorso-opening-cta"
            className="inline-flex items-center gap-3 bg-[#B40101] hover:bg-[#8F0101] text-white font-semibold text-base md:text-lg px-6 md:px-8 py-3.5 md:py-4 rounded-xl transition-colors shadow-sm"
          >
            <span>Inizia dalla tua situazione</span>
            <ArrowRight className="w-5 h-5" strokeWidth={2.2} />
          </Link>
        </div>
        <p
          data-testid="percorso-opening-microcopy"
          className="mt-5 text-sm text-[#0A0A0A]/70 leading-relaxed max-w-2xl"
        >
          Se ne hai bisogno, orientamento gratuito · Nessuna registrazione ·
          Le tue informazioni restano private
        </p>
      </div>
    </section>
  );
}

// ---------- 2. LE TRE DOMANDE (parallax on desktop, stacked on mobile) ----

const TRE_DOMANDE = [
  "Se questa casa smettesse di essere una preoccupazione, che cosa vorresti che diventasse per te?",
  "Tra dodici mesi, quale risultato ti farebbe dire: ho scelto bene?",
  "Quale informazione, se l’avessi oggi, cambierebbe davvero la tua decisione?",
];

function TreDomandeSection() {
  return (
    <section
      data-testid="percorso-tre-domande"
      className="relative border-b border-[#E5E7EB] bg-[#F5F1EA] overflow-hidden"
    >
      {/* Desktop: fixed parallax bg image */}
      <div
        aria-hidden="true"
        className="hidden md:block absolute inset-0 -z-0 bg-center bg-cover bg-fixed"
        style={{
          backgroundImage:
            "url('/images/percorso/vg-percorso-tre-domande.webp')",
        }}
      />
      <div
        aria-hidden="true"
        className="hidden md:block absolute inset-0 -z-0 bg-gradient-to-r from-white/95 via-white/80 to-white/10"
      />

      {/* Mobile: full image on top (no parallax) */}
      <img
        src="/images/percorso/vg-percorso-tre-domande.webp"
        alt=""
        loading="lazy"
        decoding="async"
        data-testid="percorso-tre-domande-image-mobile"
        className="md:hidden w-full aspect-[4/3] object-cover object-[62%_center]"
      />

      <div className="relative max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-28 lg:py-32">
        <div className="max-w-[560px] md:max-w-[520px]">
          <Eyebrow testid="percorso-tre-domande-eyebrow">
            Le tre domande
          </Eyebrow>
          <SectionTitle testid="percorso-tre-domande-h2" size="xl">
            Prima della casa, ci sei tu.
          </SectionTitle>
          <ol
            data-testid="percorso-tre-domande-list"
            className="mt-8 md:mt-10 space-y-6 md:space-y-7"
          >
            {TRE_DOMANDE.map((q, i) => (
              <li
                key={i}
                data-testid={`percorso-tre-domande-item-${i + 1}`}
                className="flex gap-4 md:gap-5"
              >
                <span
                  aria-hidden="true"
                  className="flex-shrink-0 w-10 h-10 md:w-11 md:h-11 rounded-full bg-[#B40101] text-white font-display font-bold text-lg flex items-center justify-center shadow-sm"
                >
                  {i + 1}
                </span>
                <p className="text-[#1F2937] text-lg md:text-xl leading-relaxed font-medium">
                  {q}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

// ---------- 3. LA RISPOSTA ------------------------------------------------

function RispostaSection() {
  return (
    <section
      data-testid="percorso-risposta"
      className="bg-white border-b border-[#E5E7EB]"
    >
      <div className="max-w-[900px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <Eyebrow testid="percorso-risposta-eyebrow">La risposta</Eyebrow>
        <SectionTitle testid="percorso-risposta-h2">
          La risposta prende forma quando tutto entra nello stesso quadro.
        </SectionTitle>
        <p
          data-testid="percorso-risposta-body"
          className="mt-6 md:mt-8 text-[#1F2937] text-lg md:text-xl leading-relaxed font-light"
        >
          Il valore apre la decisione. Il tuo obiettivo le dà una
          direzione. Tempi, documenti, costi e mercato mostrano quanto quella
          strada sia percorribile. ValutaGenio riunisce queste informazioni e
          le trasforma in un prossimo passo concreto.
        </p>
      </div>
    </section>
  );
}

// ---------- 4. INFOGRAFICA ------------------------------------------------

const STEPS = [
  {
    key: "domanda",
    label: "La tua domanda",
    icon: HelpCircle,
    caption: "Che cosa vuoi ottenere?",
    tint: "bg-[#F5F1EA]",
    ring: "ring-[#D6C9AE]",
    dot: "text-[#B40101]",
  },
  {
    key: "capisci",
    label: "CAPISCI",
    icon: Compass,
    caption: "Obiettivo, situazione e tempi.",
    tint: "bg-[#FFF4F0]",
    ring: "ring-[#F4C7B8]",
    dot: "text-[#B40101]",
  },
  {
    key: "valuta",
    label: "VALUTA",
    icon: Ruler,
    caption: "Valore, potenziale e scenari.",
    tint: "bg-[#EEF2FF]",
    ring: "ring-[#C7D2FE]",
    dot: "text-[#4338CA]",
  },
  {
    key: "decidi",
    label: "DECIDI",
    icon: GitCompareArrows,
    caption: "Alternative confrontate e priorità.",
    tint: "bg-[#ECFDF5]",
    ring: "ring-[#A7F3D0]",
    dot: "text-[#047857]",
  },
  {
    key: "agisci",
    label: "AGISCI",
    icon: ClipboardList,
    caption: "Piano, verifiche e persone da coinvolgere.",
    tint: "bg-[#FEF3C7]",
    ring: "ring-[#FDE68A]",
    dot: "text-[#B45309]",
  },
];

function StepCard({ step, index, testid }) {
  const Icon = step.icon;
  return (
    <div
      data-testid={testid}
      className="relative flex flex-col items-center text-center lg:w-40 xl:w-44"
    >
      <div
        className={`w-20 h-20 md:w-24 md:h-24 rounded-full ${step.tint} ring-2 ${step.ring} flex items-center justify-center shadow-sm`}
        aria-hidden="true"
      >
        <Icon className={`w-9 h-9 md:w-10 md:h-10 ${step.dot}`} strokeWidth={1.8} />
      </div>
      <div className="mt-3 flex items-center gap-2">
        <span className="text-xs md:text-sm font-mono text-[#0A0A0A]/60">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span
          className={`font-display font-extrabold tracking-wide ${
            index === 0 ? "text-base" : "text-lg md:text-xl"
          } text-[#1F2937]`}
        >
          {step.label}
        </span>
      </div>
      <p className="mt-2 text-sm md:text-[15px] text-[#0A0A0A] leading-relaxed max-w-[220px]">
        {step.caption}
      </p>
    </div>
  );
}

function InfograficaSection() {
  return (
    <section
      data-testid="percorso-infografica"
      className="bg-[#FAFAF7] border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <Eyebrow testid="percorso-infografica-eyebrow">Infografica</Eyebrow>
        <SectionTitle testid="percorso-infografica-h2">
          Dal dubbio a una direzione concreta
        </SectionTitle>

        {/* Desktop: horizontal timeline */}
        <div
          className="hidden lg:block mt-14 xl:mt-16"
          data-testid="percorso-infografica-desktop"
        >
          <div className="relative">
            <div
              aria-hidden="true"
              className="absolute left-0 right-0 top-12 h-[2px] bg-gradient-to-r from-[#E5D8C1] via-[#C7D2FE] to-[#FDE68A]"
            />
            <div className="relative flex items-start justify-between gap-4 xl:gap-6">
              {STEPS.map((s, i) => (
                <React.Fragment key={s.key}>
                  <StepCard
                    step={s}
                    index={i}
                    testid={`percorso-infografica-step-${s.key}`}
                  />
                  {i < STEPS.length - 1 && (
                    <div
                      aria-hidden="true"
                      className="flex-1 flex items-center justify-center pt-11"
                    >
                      <ArrowRight
                        className="w-5 h-5 text-[#0A0A0A]/40"
                        strokeWidth={2}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile / tablet: vertical stack */}
        <div
          className="lg:hidden mt-12 space-y-8"
          data-testid="percorso-infografica-mobile"
        >
          {STEPS.map((s, i) => (
            <div key={s.key} className="relative flex gap-5 md:gap-6 items-start">
              <div className="flex flex-col items-center">
                <div
                  className={`w-16 h-16 md:w-20 md:h-20 rounded-full ${s.tint} ring-2 ${s.ring} flex items-center justify-center shadow-sm flex-shrink-0`}
                  aria-hidden="true"
                >
                  <s.icon
                    className={`w-7 h-7 md:w-8 md:h-8 ${s.dot}`}
                    strokeWidth={1.8}
                  />
                </div>
                {i < STEPS.length - 1 && (
                  <div
                    aria-hidden="true"
                    className="mt-2 w-[2px] flex-1 min-h-[36px] bg-[#E5E7EB]"
                  />
                )}
              </div>
              <div className="pb-6">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-[#0A0A0A]/60">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span
                    className={`font-display font-extrabold tracking-wide ${
                      i === 0 ? "text-base" : "text-lg"
                    } text-[#1F2937]`}
                  >
                    {s.label}
                  </span>
                </div>
                <p className="mt-1.5 text-[15px] md:text-base text-[#0A0A0A] leading-relaxed">
                  {s.caption}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- 5. PROPERTY ADVISOR ------------------------------------------

function PropertyAdvisorSection() {
  return (
    <section
      data-testid="percorso-property-advisor"
      className="bg-white border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] gap-10 md:gap-14 lg:gap-16 items-center">
          <div className="order-2 lg:order-1">
            <Eyebrow testid="percorso-pa-eyebrow">Property Advisor</Eyebrow>
            <SectionTitle testid="percorso-pa-h2">
              Quando l’analisi incontra la realtà
            </SectionTitle>
            <div
              data-testid="percorso-pa-body"
              className="mt-6 md:mt-8 space-y-5 text-[#1F2937] text-lg leading-relaxed font-light"
            >
              <p>
                ValutaGenio individua il potenziale e apre gli scenari. Il
                Property Advisor li porta dentro la vita reale della persona.
              </p>
              <p>
                Legge l’analisi insieme a te, conosce il territorio e
                verifica ciò che può accelerare o bloccare il
                percorso. Ti aiuta a ordinare tempi, documenti e professionisti
                da coinvolgere. La decisione resta tua.
              </p>
              <p>
                Il confronto iniziale è gratuito e parte soltanto da una
                tua richiesta. Dare chiarezza crea un terreno migliore, dove
                proprietari e professionisti possono lavorare con obiettivi
                chiari, informazioni utili e responsabilità definite.
              </p>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <div className="relative rounded-2xl overflow-hidden shadow-md ring-1 ring-black/5">
              <img
                src="/images/percorso/vg-percorso-property-advisor.webp"
                alt=""
                loading="lazy"
                decoding="async"
                data-testid="percorso-pa-image"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- 6. CTA FINALE -------------------------------------------------

function FinalCta() {
  return (
    <section data-testid="percorso-final-cta" className="bg-[#F9F9F9]">
      <div className="max-w-[900px] mx-auto px-5 md:px-8 py-16 md:py-24 text-center">
        <SectionTitle testid="percorso-final-cta-h2" size="xl">
          Adesso parti da ciò che vuoi ottenere.
        </SectionTitle>
        <p
          data-testid="percorso-final-cta-body"
          className="mt-6 md:mt-8 text-[#1F2937] text-lg md:text-xl leading-relaxed font-light max-w-2xl mx-auto"
        >
          La prima domanda richiede pochi secondi. Da lì ValutaGenio
          costruisce il percorso intorno alla tua situazione.
        </p>
        <div className="mt-8 md:mt-10 flex justify-center">
          <a
            href="/#inizia"
            data-testid="percorso-final-cta-button"
            className="inline-flex items-center gap-3 bg-[#B40101] hover:bg-[#8F0101] text-white font-semibold text-base md:text-lg px-6 md:px-8 py-3.5 md:py-4 rounded-xl transition-colors shadow-sm"
          >
            <span>Inizia dalla tua casa</span>
            <ArrowRight className="w-5 h-5" strokeWidth={2.2} />
          </a>
        </div>
      </div>
    </section>
  );
}

// ---------- page shell ----------------------------------------------------

export default function Percorso() {
  return (
    <main data-testid="percorso-page" className="min-h-screen bg-white">
      <LandingNavbar />
      <OpeningSection />
      <TreDomandeSection />
      <RispostaSection />
      <InfograficaSection />
      <PropertyAdvisorSection />
      <FinalCta />
      <Footer />
    </main>
  );
}
